import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Copy, Loader2, Plus, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/ui";
import { cn } from "@/lib/cn";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useDeletePage, useDuplicatePage } from "@/data/hooks";
import { builder, type Page } from "@/data/services";

/* --------------------------------- status --------------------------------- */
const STATUS: Record<Page["status"], { label: string; cls: string }> = {
  published: { label: "Live", cls: "bg-accent-soft text-accent" },
  draft: { label: "Draft", cls: "bg-paper-2 text-muted dark:bg-white/10" },
  staging: { label: "Staging", cls: "bg-paper-2 text-ink dark:bg-white/10" },
  archived: { label: "Archived", cls: "bg-paper-2 text-subtle dark:bg-white/5" },
};

/** A draft/live/staging pill in the v2 palette. Exported so the context bar can
 *  show the active page's status next to its title without a second component. */
export function StatusPill({ status }: { status: Page["status"] }) {
  const s = STATUS[status] ?? STATUS.draft;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide",
        s.cls,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {s.label}
    </span>
  );
}

/** title → URL-safe slug (mirrors the backend's expectations loosely). */
function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "page"
  );
}

/**
 * The Make page context picker. It is the single answer to "which page am I
 * editing?": a trigger that shows the active page (title · /slug · status) and a
 * popover to SWITCH between the project's pages or create a NEW one. Creating a
 * page POSTs to the builder, refetches the list, and selects the new page so the
 * marketer can start editing it immediately.
 */
export function PagePicker({
  pages,
  activePageId,
  onSelect,
}: {
  pages: Page[];
  activePageId: string | undefined;
  onSelect: (id: string) => void;
}) {
  const { current, currentProjectId } = useWorkspace();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const del = useDeletePage(currentProjectId);
  const dup = useDuplicatePage(currentProjectId);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const active = pages.find((p) => p.id === activePageId);

  // Open the popover anchored to the trigger, in a portal (so it floats above
  // the preview / inspector / nav instead of being trapped behind them).
  const toggle = () => {
    if (open) return setOpen(false);
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      const width = Math.min(320, window.innerWidth - 24);
      const left = Math.min(r.left, window.innerWidth - width - 12);
      setPos({ top: r.bottom + 8, left: Math.max(12, left), width });
    }
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setCreating(false);
    setConfirmDeleteId(null);
    setErr(null);
  };

  // Duplicate a page → clone into a new draft and switch to it.
  const handleDuplicate = async (p: Page) => {
    try {
      const np = await dup.mutateAsync({ pageId: p.id });
      await qc.invalidateQueries({ queryKey: ["pages", currentProjectId] });
      onSelect(np.id);
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't duplicate the page.");
    }
  };

  // Delete a page. If it was the one being edited, switch to another first so
  // Make never points at a deleted page.
  const handleDelete = (p: Page) => {
    const nextId = pages.find((q) => q.id !== p.id)?.id;
    del.mutate(p.id, {
      onSuccess: () => {
        setConfirmDeleteId(null);
        setErr(null);
        if (p.id === activePageId && nextId) onSelect(nextId);
      },
      onError: (e) => {
        setConfirmDeleteId(null);
        setErr(e instanceof Error ? e.message : "Couldn't delete the page.");
      },
    });
  };

  const handleCreate = async () => {
    const t = title.trim();
    if (!t || !currentProjectId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const domain = current.domain.includes(".") ? current.domain : "aigen.solutions";
      const page = await builder.createPage(currentProjectId, {
        title: t,
        slug: slugify(t),
        domain,
      });
      await qc.invalidateQueries({ queryKey: ["pages", currentProjectId] });
      onSelect(page.id);
      setTitle("");
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't create the page.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-full items-center gap-2.5 rounded-btn border border-hairline bg-paper px-3 py-2 text-left shadow-sm transition-colors hover:border-ink/25 dark:bg-white/5 dark:shadow-none"
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[14px] font-semibold leading-tight text-ink-strong">
            {active?.title ?? "Select a page"}
          </span>
          {active && (
            <span className="truncate font-mono text-[11px] leading-tight text-subtle">
              /{active.slug}
            </span>
          )}
        </span>
        {active && <StatusPill status={active.status} />}
        <ChevronDown className="h-4 w-4 shrink-0 text-subtle" strokeWidth={2} />
      </button>

      {open && pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[60]" onClick={close} aria-hidden />
            <div
              className="fixed z-[61] overflow-hidden rounded-card border border-hairline bg-paper shadow-xl dark:bg-ink-2"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
            <p className="eyebrow border-b border-hairline px-3 py-2">Switch page</p>
            {err && !creating && (
              <p className="px-3 pt-2 text-[11px] leading-relaxed text-accent">{err}</p>
            )}
            <ul className="max-h-[280px] overflow-auto p-1.5" role="listbox">
              {pages.length === 0 && (
                <li className="px-2.5 py-4 text-center text-[13px] text-muted">No pages yet.</li>
              )}
              {pages.map((p) => {
                const sel = p.id === activePageId;
                const confirming = confirmDeleteId === p.id;
                return (
                  <li
                    key={p.id}
                    className={cn(
                      "group flex items-center gap-0.5 rounded-btn transition-colors",
                      sel ? "bg-accent-soft" : "hover:bg-paper-2 dark:hover:bg-white/5",
                    )}
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={sel}
                      onClick={() => {
                        onSelect(p.id);
                        close();
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-btn px-2.5 py-2 text-left"
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span
                          className={cn(
                            "truncate text-[13px] font-medium",
                            sel ? "text-accent" : "text-ink",
                          )}
                        >
                          {p.title}
                        </span>
                        <span className="truncate font-mono text-[10px] text-subtle">
                          /{p.slug}
                        </span>
                      </span>
                      <StatusPill status={p.status} />
                      {sel && (
                        <Check className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2.25} />
                      )}
                    </button>
                    {confirming ? (
                      <span className="flex shrink-0 items-center gap-0.5 pr-1.5">
                        <button
                          type="button"
                          title="Confirm delete"
                          disabled={del.isPending}
                          onClick={() => handleDelete(p)}
                          className="rounded p-1 text-accent hover:bg-accent/10 disabled:opacity-50"
                        >
                          {del.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                          ) : (
                            <Check className="h-3.5 w-3.5" strokeWidth={2} />
                          )}
                        </button>
                        <button
                          type="button"
                          title="Cancel"
                          onClick={() => setConfirmDeleteId(null)}
                          className="rounded p-1 text-subtle hover:bg-ink/5 hover:text-ink"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </span>
                    ) : (
                      <span className="flex shrink-0 items-center pr-1.5 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
                        <button
                          type="button"
                          title="Duplicate page"
                          disabled={dup.isPending}
                          onClick={() => handleDuplicate(p)}
                          className="rounded p-1 text-subtle hover:bg-ink/5 hover:text-ink disabled:opacity-50"
                        >
                          {dup.isPending && dup.variables?.pageId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                          ) : (
                            <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
                          )}
                        </button>
                        <button
                          type="button"
                          title="Delete page"
                          onClick={() => setConfirmDeleteId(p.id)}
                          className="rounded p-1 text-subtle hover:bg-accent/10 hover:text-accent"
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="border-t border-hairline p-1.5">
              {creating ? (
                <div className="space-y-2 p-1.5">
                  <input
                    autoFocus
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") {
                        setCreating(false);
                        setErr(null);
                      }
                    }}
                    placeholder="New page title…"
                    className="w-full rounded-btn border border-hairline bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-subtle focus:border-accent/50 dark:bg-white/5"
                  />
                  {title.trim() && (
                    <p className="px-0.5 font-mono text-[10px] text-subtle">/{slugify(title)}</p>
                  )}
                  {err && <p className="px-0.5 text-[11px] leading-relaxed text-accent">{err}</p>}
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCreating(false);
                        setErr(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="coral"
                      size="sm"
                      onClick={handleCreate}
                      disabled={busy || !title.trim()}
                    >
                      {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
                      ) : (
                        "Create page"
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-2 rounded-btn px-2.5 py-2 text-[13px] font-medium text-muted transition-colors hover:bg-paper-2 hover:text-accent dark:hover:bg-white/5"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.75} />
                  New page
                </button>
              )}
            </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
